import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Grid } from '@material-ui/core';

import { CorporateAccount } from '@lib/state';
import { FormCheckbox, FormTextField } from '@lib/common';

interface Props {
  account?: CorporateAccount;
}

export const CorporateAccountInformationForm: React.FC<Props> = ({ account }) => {
  const { errors } = useFormContext();

  return (
    <Grid container spacing={1}>
      <Grid item md={6} xs={12}>
        <FormTextField
          name="name"
          label="Name"
          defaultValue={account?.name}
          fullWidth
          validationOptions={{ required: 'This field is required.' }}
          required
          data-testid="corporateAccountNameInput"
        />
      </Grid>
      <Grid item md={6} xs={12}>
        <FormTextField
          name="invoiceSubAccount"
          label="Invoice Account"
          defaultValue={account?.invoiceSubAccount}
          fullWidth
          required={account ? true : false}
          validationOptions={{
            pattern: /^[a-zA-Z0-9]{0,8}$/,
          }}
          helperText={errors =>
            errors.invoiceSubAccount?.type === 'pattern'
              ? 'Invoice account has a max length of 8 characters and must be alphanumeric.'
              : account
              ? ''
              : 'If left blank will be automatically generated based on name'
          }
          data-testid="corporateAccountInvoiceSubInput"
        />
      </Grid>
      {!!account?.id && (
        <>
          <Grid item md={6} xs={12}>
            <FormTextField
              name="reportRecipients"
              label="Report Recipients"
              defaultValue={account?.reportRecipients}
              fullWidth
              validationOptions={{
                pattern: /^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,10}[\W]*;{1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,10})[\W]*$/,
              }}
              helperText={errors =>
                errors.reportRecipients?.type === 'pattern'
                  ? 'Please enter a semicolon separated list ex. jdoe@domain.com; jdoe2@domain.com'
                  : 'Report Recipients are used for email report delivery instead of using the delegate email addresses.'
              }
              data-testid="corporateReportRecipientsInput"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormTextField
              name="bookingRecipients"
              label="Booking Recipients"
              defaultValue={account?.bookingRecipients}
              fullWidth
              validationOptions={{
                pattern: /^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,10}[\W]*;{1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,10})[\W]*$/,
              }}
              helperText={errors =>
                errors.bookingRecipients?.type === 'pattern'
                  ? 'Please enter a semicolon separated list ex. jdoe@domain.com; jdoe2@domain.com'
                  : 'Booking Recipients are copied for booking notifications (create, update, cancel).'
              }
              data-testid="corporateBookingRecipientsInput"
            />
          </Grid>
        </>
      )}
      <Grid item md={6} xs={12}>
        <FormCheckbox
          name="disableEmployeeBooking"
          label="Disable Employee Booking"
          defaultChecked={account?.disableEmployeeBooking}
          fullWidth
          helperText={
            errors.disableEmployeeBooking?.message ??
            'If enabled, then front desk and manager employees cannot book on behalf of any delegate assigned to this corporate account.'
          }
          data-testid="corporateDisableEmployeeBookingCheckbox"
        />
      </Grid>
      {!!account?.id && (
        <>
          <Grid item md={3} xs={12}>
            <FormCheckbox
              name="sendDailyHousingReport"
              label="Send Daily Housing Report"
              defaultChecked={account?.sendDailyHousingReport}
              fullWidth
              data-testid="corporateDailyReportCheckbox"
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <FormCheckbox
              name="enableSleepSchedule"
              label="Enable Sleeping Schedules"
              defaultChecked={account?.enableSleepSchedule}
              fullWidth
              helperText={
                errors.enableSleepSchedule?.message ??
                'If enabled, then Day or Night Sleeper must be selected for the guest while booking.'
              }
              data-testid="corporateSleepScheduleCheckbox"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormCheckbox
              name="isAutomaticLateCheckoutEnabled"
              label="Automatic Late Checkout For Day Sleepers"
              defaultChecked={account?.isAutomaticLateCheckoutEnabled}
              fullWidth
              helperText={
                errors.isAutomaticLateCheckoutEnabled?.message ??
                'If enabled, then Late Checkout is applied automatically for Day Sleepers when booking.'
              }
              data-testid="corporateLateCheckoutCheckbox"
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
